/**
 * /case/draft: the case page for a bill that hasn't been paid for yet
 * (2026-10-05, reworked 2026-10-27). A free account uploads, lands here,
 * and sees its case laid out like a real one: the bill, the first look,
 * what happens when they start.
 *
 * They can also message us from here before paying. Those messages go in
 * lead_messages (migration 110), show up under the lead in the admin
 * Leads tab, and get answered by hand. Our replies land back here live.
 *
 * Phone numbers (2026-11-02): right after they send a message we offer a
 * text when the team replies. It's the one moment a number is obviously
 * useful to them, and nobody who doesn't message ever sees the ask.
 * Tapping "Text me" is the SMS opt-in (sms_consent_at, migration 040).
 *
 * Phone required (2026-11-02): every lead needs a number so we can call
 * them. /start asks for it before they sign in, so this is the backup:
 * anyone who still has no phone_e164 (an older lead, or the number got
 * lost on the way) gets one short ask in the thread before the first
 * look shows. The check still runs underneath,
 * so the first look is usually ready the moment they save. Giving the
 * number here is NOT the texting opt-in, that's still the "Text me" step.
 *
 * It borrows the case page's classes (case-page.css) so the two look the
 * same. The bill itself is the parked upload in bill_drafts, not a bills
 * row. Nothing here is a real case yet, see migration 103.
 *
 * Many bills (2026-11-02, migration 115): an account can park several.
 * /case/draft?id=<draft id> opens that one, no id opens the newest. With
 * more than one there's a short list in the side panel to switch between
 * them. The message thread is per account, not per bill.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Eye, FileText, FolderOpen, Lock, Plus, Send, X as XIcon } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { shrinkImage } from '../lib/shrinkImage';
import { toE164 } from '../lib/phone';
import { API_BASE } from '../lib/apiConfig';
import AppSidebar from '../components/AppSidebar';
import StartCasePaywall from '../components/StartCasePaywall';
import ExampleCaseModal from '../components/ExampleCaseModal';
import { SPECIALIST } from '../lib/specialist';
import {
  deleteBillDraft,
  loadBillDrafts,
  runFreeCheck,
  type BillDraft,
  type FreeCheckResult,
} from '../lib/freeAccount';
import { phFreeLookAction, phFreeLookOfferSeen, phFreeLookViewed, phQuestionOpened } from '../lib/posthog';
import { updateIntercom } from '../lib/intercom';
import '@styles/pages/case-page.css';
import '@styles/components/start-case-paywall.css';
import '@styles/pages/draft-case.css';

const READING_STEPS = ['Reading your bill', 'Checking each line', 'Comparing prices'];

/** What happens after they start, in the case page's own stage wording. */
const AHEAD = ['Reviewing your bill', 'Working on your resolution', 'Confirming the outcome', 'Resolved'];

const QUESTION_CHIPS = ['Is my bill a good fit?', 'How long does it take?', 'Should I pay it in the meantime?'];

/** Writes to the user's profile. Some free accounts may not have a
    profiles row yet, so if the update touches nothing it makes one. */
async function saveProfile(userId: string, patch: Record<string, unknown>): Promise<boolean> {
  const { data, error } = await supabase.from('profiles').update(patch).eq('id', userId).select('id');
  if (!error && data && data.length > 0) return true;
  const { error: upsertError } = await supabase.from('profiles').upsert({ id: userId, ...patch });
  return !upsertError;
}

const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

/** Whole days from today to a YYYY-MM-DD date. Negative once it's past. */
const daysUntil = (iso: string) =>
  Math.ceil((new Date(`${iso}T12:00:00`).getTime() - Date.now()) / 86_400_000);
const shortDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

type LeadMessage = {
  id: string;
  sender_type: 'customer' | 'admin';
  sender_name: string | null;
  message: string;
  created_at: string;
  /** On our replies, when they saw it here (migration 119). */
  read_at?: string | null;
};

const timeOf = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export default function DraftCase() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  // The sign-in refreshes about once an hour and hands back a new user
  // object each time. Keying the load on the id stops that from re-running
  // the first look and logging free_look_viewed again on a tab left open.
  const userId = user?.id ?? null;
  const [searchParams] = useSearchParams();
  const wantedId = searchParams.get('id');

  const [draft, setDraft] = useState<BillDraft | null>(null);
  // All their parked bills, newest first, for the switcher.
  const [drafts, setDrafts] = useState<BillDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<FreeCheckResult | null>(null);
  const [readingStep, setReadingStep] = useState(0);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallTrigger, setPaywallTrigger] = useState('draft_case');
  // On a phone the side panel is hidden, so it opens as a sheet instead.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  // The start button in the offer card. The small one by the message box
  // only shows once this one is off screen.
  const offerCtaRef = useRef<HTMLButtonElement | null>(null);
  const [offerCtaVisible, setOfferCtaVisible] = useState(true);
  const offerSeenSent = useRef(false);

  // Messages to the team.
  const [messages, setMessages] = useState<LeadMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // The conversion bits (2026-10-27): the example case, real results,
  // the due date and whether Annual is worth mentioning.
  const [showExample, setShowExample] = useState(false);
  const [results, setResults] = useState<{ cases: number; medianSaved: number | null } | null>(null);
  const [familyCount, setFamilyCount] = useState(0);
  const [dueInput, setDueInput] = useState('');
  const [savingDue, setSavingDue] = useState(false);

  // "Text me when they reply". textsOn is null until the profile loads, so
  // the ask doesn't flash up for someone who already said yes.
  const [textsOn, setTextsOn] = useState<boolean | null>(null);
  // Whether there's a phone on the profile. Null while loading. False shows
  // the phone ask in place of the first look.
  const [hasPhone, setHasPhone] = useState<boolean | null>(null);
  const [gatePhone, setGatePhone] = useState('');
  const [savingGate, setSavingGate] = useState(false);
  const [gateError, setGateError] = useState<string | null>(null);
  const gateSeenSent = useRef(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneJustSaved, setPhoneJustSaved] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    supabase
      .from('profiles')
      .select('phone_e164, sms_consent_at, sms_opt_out_at')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        // Someone who replied STOP doesn't get asked again here.
        setTextsOn(Boolean(data?.phone_e164 && (data.sms_opt_out_at || data.sms_consent_at)));
        setHasPhone(Boolean(data?.phone_e164));
        if (data?.phone_e164) setPhoneInput(data.phone_e164.replace(/^\+1/, ''));
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // The required phone ask before the first look.
  useEffect(() => {
    if (hasPhone === false && !gateSeenSent.current) {
      gateSeenSent.current = true;
      phFreeLookAction('phone_gate_shown');
    }
  }, [hasPhone]);

  const saveGatePhone = async () => {
    if (!userId || savingGate) return;
    const phone = toE164(gatePhone);
    if (!phone) {
      setGateError('Enter a US phone number, like (555) 123-4567.');
      return;
    }
    setSavingGate(true);
    setGateError(null);
    let tz: string | null = null;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      tz = null;
    }
    const ok = await saveProfile(userId, {
        phone_e164: phone,
        // Marks the SMS consent pop-up as seen so it doesn't jump out the
        // moment they save. Texting still needs the "Text me" step.
        sms_consent_prompt_seen_at: new Date().toISOString(),
        ...(tz ? { timezone: tz } : {}),
    });
    setSavingGate(false);
    if (!ok) {
      setGateError("Couldn't save that. Try again in a moment.");
      return;
    }
    setHasPhone(true);
    setPhoneInput(phone.replace(/^\+1/, ''));
    phFreeLookAction('phone_gate_saved');
    updateIntercom({ phone });
  };

  const savePhone = async () => {
    if (!userId || savingPhone) return;
    const phone = toE164(phoneInput);
    if (!phone) {
      setPhoneError('Enter a US mobile number, like (555) 123-4567.');
      return;
    }
    setSavingPhone(true);
    setPhoneError(null);
    const nowIso = new Date().toISOString();
    let tz: string | null = null;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
      tz = null;
    }
    const ok = await saveProfile(userId, {
        phone_e164: phone,
        sms_consent_at: nowIso,
        sms_consent_prompt_seen_at: nowIso,
        sms_consent_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        ...(tz ? { timezone: tz } : {}),
    });
    setSavingPhone(false);
    if (!ok) {
      setPhoneError("Couldn't save that. Try again in a moment.");
      return;
    }
    setTextsOn(true);
    setPhoneJustSaved(true);
    phFreeLookAction('phone_added');
  };

  useEffect(() => {
    fetch(`${API_BASE}/free/results`)
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => b && setResults(b))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!userId) return;
    supabase
      .from('family_members')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .then(({ count }) => setFamilyCount(count ?? 0));
  }, [userId]);
  const threadRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      navigate('/login', { replace: true });
      return;
    }
    let cancelled = false;

    // Switching to another bill starts this page over for that one.
    setLoading(true);
    setResult(null);
    setReadingStep(0);
    setConfirmReplace(false);
    setDueInput('');

    (async () => {
      const [all, subs] = await Promise.all([
        loadBillDrafts(userId),
        supabase.from('subscriptions').select('id').eq('user_id', userId).limit(1),
      ]);
      if (cancelled) return;
      // The one in the link, or the newest if there's no id or it's gone.
      const parked = all.find((d) => d.id === wantedId) ?? all[0] ?? null;

      // No upload, or already paid: the upload form handles both.
      if (!parked || (subs.data && subs.data.length > 0)) {
        navigate('/add-bill', { replace: true });
        return;
      }

      setDrafts(all);
      setDraft(parked);
      setLoading(false);

      const checkStarted = Date.now();
      const r = await runFreeCheck(parked.id);
      if (cancelled) return;
      setResult(r);
      updateIntercom({
        free_bill: parked.provider_name || (r.status === 'ok' ? r.look.provider : null) || parked.file_name || 'uploaded',
        first_look: r.status,
        first_look_findings: r.status === 'ok' ? r.look.findings.length : 0,
        bill_due: parked.due_date,
      });
      const okLook = r.status === 'ok' ? r.look : null;
      phFreeLookViewed({
        status: r.status,
        findings: okLook ? okLook.findings.length : 0,
        amountInQuestion: okLook ? okLook.amountInQuestion : null,
        itemized: okLook ? okLook.itemized : null,
        checks: okLook ? okLook.checks : null,
        moreFindings: okLook ? okLook.moreFindings : null,
        amountDue: okLook?.amountDue ?? null,
        estimateLow: okLook?.estimate?.low ?? null,
        estimateHigh: okLook?.estimate?.high ?? null,
        msToResult: Date.now() - checkStarted,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, userId, wantedId, navigate]);

  // The conversation with the team, and our replies as they come in.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    supabase
      .from('lead_messages')
      .select('id, sender_type, sender_name, message, created_at, read_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        if (!cancelled && data) setMessages(data as LeadMessage[]);
      });

    const channel = supabase
      .channel(`lead-messages-${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'lead_messages', filter: `user_id=eq.${userId}` },
        (payload) => {
          const row = payload.new as LeadMessage;
          setMessages((prev) => (prev.some((m) => m.id === row.id) ? prev : [...prev, row]));
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [userId]);

  // The message box replaces the Intercom bubble on this page. It also sat
  // right on top of the side panel.
  useEffect(() => {
    updateIntercom({ hide_default_launcher: true });
    return () => updateIntercom({ hide_default_launcher: false });
  }, []);

  // Read receipts (2026-11-03). Being on this page with the tab in front
  // counts as seeing our replies, the thread sits right on the page. The
  // team sees "Seen" under their message in the admin.
  const unseenReplies = messages.some((m) => m.sender_type === 'admin' && !m.read_at);
  useEffect(() => {
    if (!userId || !unseenReplies) return;
    let done = false;
    const mark = () => {
      if (done || document.visibilityState !== 'visible') return;
      done = true;
      const now = new Date().toISOString();
      setMessages((prev) => prev.map((m) => (m.sender_type === 'admin' && !m.read_at ? { ...m, read_at: now } : m)));
      void supabase.rpc('mark_lead_replies_read').then(() => undefined, () => undefined);
    };
    mark();
    document.addEventListener('visibilitychange', mark);
    return () => document.removeEventListener('visibilitychange', mark);
  }, [userId, unseenReplies]);

  // Keep the newest message in view.
  useEffect(() => {
    if (!messages.length) return;
    const el = threadRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  useEffect(() => {
    if (result || loading) return;
    const t = setInterval(() => setReadingStep((s) => Math.min(s + 1, READING_STEPS.length - 1)), 9000);
    return () => clearInterval(t);
  }, [result, loading]);

  useEffect(() => {
    const el = offerCtaRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([entry]) => {
        setOfferCtaVisible(entry.isIntersecting);
        if (entry.isIntersecting && !offerSeenSent.current && result) {
          offerSeenSent.current = true;
          const okLook = result.status === 'ok' ? result.look : null;
          phFreeLookOfferSeen({
            findings: okLook ? okLook.findings.length : 0,
            estimateShown: Boolean(okLook?.estimate),
          });
        }
      },
      { root: threadRef.current, threshold: 0.5 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [result]);

  const start = (trigger: string) => {
    setPaywallTrigger(trigger);
    setShowPaywall(true);
  };

  const send = useCallback(async () => {
    const body = text.trim();
    if (!body || !userId || sending) return;
    setSending(true);
    setSendError(null);
    const { data, error } = await supabase
      .from('lead_messages')
      .insert({ user_id: userId, sender_type: 'customer', message: body.slice(0, 4000) })
      .select('id, sender_type, sender_name, message, created_at, read_at')
      .single();
    setSending(false);
    if (error || !data) {
      setSendError("That didn't send. Try again in a moment.");
      return;
    }
    setText('');
    setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data as LeadMessage]));
    phQuestionOpened({ source: messages.some((m) => m.sender_type === 'customer') ? 'draft_case_reply' : 'draft_case_message' });

    // Lets the team know there's a message waiting. Best effort.
    try {
      const { data: s } = await supabase.auth.getSession();
      const token = s.session?.access_token;
      if (token) {
        void fetch(`${API_BASE}/notify/lead-message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ messageId: data.id }),
        }).catch(() => {});
      }
    } catch {
      /* the message is saved either way */
    }
  }, [text, userId, sending, messages]);

  const openFile = async (path: string) => {
    phFreeLookAction('open_document');
    const { data } = await supabase.storage.from('medical_bills').createSignedUrl(path, 300);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener');
  };

  /* They can add more pages or their EOB before paying. It all goes to
     the specialist with the case. The first look isn't re-run. */
  const pageInputRef = useRef<HTMLInputElement | null>(null);
  const eobInputRef = useRef<HTMLInputElement | null>(null);
  const [adding, setAdding] = useState<'page' | 'eob' | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  const addDocument = async (kind: 'page' | 'eob', picked: File | undefined) => {
    if (!draft || !userId || !picked) return;
    setAdding(kind);
    setAddError(null);
    const file = kind === 'page' ? await shrinkImage(picked) : picked;
    const ts = Date.now();
    const path = kind === 'page'
      ? `${userId}/${ts}-p${draft.extra_files.length + 2}-${file.name}`
      : `eob/${userId}/${ts}-${file.name}`;
    const { error: upErr } = await supabase.storage.from('medical_bills').upload(path, file);
    if (upErr) {
      setAddError("That file didn't upload. Try again.");
      setAdding(null);
      return;
    }
    const patch = kind === 'page'
      ? { extra_files: [...draft.extra_files, { path, name: file.name }] }
      : { eob_path: path };
    const { error: saveErr } = await supabase
      .from('bill_drafts')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', draft.id);
    setAdding(null);
    if (saveErr) {
      setAddError("That file didn't save. Try again.");
      return;
    }
    setDraft({ ...draft, ...patch });
    phFreeLookAction(kind === 'page' ? 'added_page' : 'added_eob');
  };

  const saveDueDate = async () => {
    if (!draft || !userId || !dueInput || savingDue) return;
    setSavingDue(true);
    const { error } = await supabase
      .from('bill_drafts')
      .update({ due_date: dueInput, updated_at: new Date().toISOString() })
      .eq('id', draft.id);
    setSavingDue(false);
    if (!error) {
      setDraft({ ...draft, due_date: dueInput });
      phFreeLookAction('due_date_added');
    }
  };

  const replaceFile = async () => {
    if (!draft) return;
    if (!confirmReplace) {
      phFreeLookAction('replace_file_asked');
      setConfirmReplace(true);
      return;
    }
    phFreeLookAction('replace_file_confirmed');
    // Just this bill. Any others they parked stay.
    await deleteBillDraft(draft, true);
    navigate('/add-bill?new=1', { replace: true });
  };

  if (loading || !draft) {
    return (
      <AppSidebar>
        <div className="cp2">
          <div className="case-loading" aria-busy="true">
            <div className="case-skeleton-hero" />
            <div className="case-skeleton-row" style={{ width: '94%' }} />
            <div className="case-skeleton-row" style={{ width: '70%' }} />
          </div>
        </div>
      </AppSidebar>
    );
  }

  const look = result?.status === 'ok' ? result.look : null;
  // The first look and the offer wait for a phone number. They can still
  // message the team, a question is worth having either way.
  const shown = Boolean(result) && hasPhone === true;
  const found = look?.findings.length ?? 0;
  const total = found + (look?.moreFindings ?? 0);
  const estimate = look?.estimate ?? null;
  /* One message for the first look. It never invents a finding. */
  // The upload isn't something we can work from (2026-11-02). No first
  // look and no offer, just say so and let them swap the file.
  const notBill = result?.status === 'not_a_bill' || result?.status === 'eob_only' || result?.status === 'nothing_to_read';

  const firstLook = !look
    ? result?.status === 'not_a_bill'
      ? "This doesn't look like a medical bill. I couldn't find a hospital or doctor's bill in it. Upload the bill itself, a photo or a PDF of the statement that asks you to pay."
      : result?.status === 'eob_only'
      ? "This looks like a statement from your insurance (an explanation of benefits), not the bill itself. Upload the bill from the hospital or doctor too, the one that asks you to pay, and I'll take a look."
      : result?.status === 'nothing_to_read'
      ? "I couldn't find any charges or amounts on this one, so there's nothing for me to check yet. Upload the full bill, ideally the page that lists what you're being charged for."
      : result?.status === 'unreadable'
      ? "I couldn't read this one automatically. That happens with blurry photos and unusual layouts. A specialist can still work from it, or you can upload a clearer copy."
      : "I couldn't finish the automatic first look on this one. Nothing's lost: a specialist reads every line by hand once you start."
    : found > 0
    ? total > 1
      ? `I ran ${look.checks} checks and found ${total} things worth questioning. Here's the first one. The rest open up when you start your case.`
      : `I ran ${look.checks} checks and found something worth questioning.`
    : look.itemized
    ? `I ran ${look.checks} checks and the arithmetic holds up. That's normal, and it isn't where most of the money is. Bills come down because the price itself is negotiable, and because hospitals have discounts and assistance they don't offer unless someone asks.`
    : `This is a summary statement, so I could only run ${look.checks} checks on it. The detail is on the itemized bill, which a specialist gets from the hospital for you. Then they go after the price itself, and the discounts hospitals don't offer unless someone asks.`;

  const provider = look?.provider || draft.provider_name || null;
  const title = provider || 'Your bill';
  const uploaded = new Date(draft.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  const hasAsked = messages.some((m) => m.sender_type === 'customer');
  const lastIsMine = messages.length > 0 && messages[messages.length - 1].sender_type === 'customer';

  const documents: { path: string; name: string; meta: string }[] = [
    { path: draft.file_path, name: draft.file_name || 'Medical bill', meta: 'Original' },
    ...draft.extra_files.map((f, i) => ({ path: f.path, name: f.name, meta: `Page ${i + 2}` })),
    ...(draft.eob_path ? [{ path: draft.eob_path, name: 'Explanation of benefits', meta: 'EOB' }] : []),
  ];

  const progressCard = (
    <div className="cp2-side-card">
      <div className="cp2-side-title">Case progress</div>
      <ol className="cp2-steps" aria-label="Case progress">
        <li className="cp2-step is-done">
          <span className="cp2-step-rail">
            <span className="cp2-step-dot"><Check size={9} strokeWidth={3.5} /></span>
            <span className="cp2-step-line" aria-hidden />
          </span>
          <span className="cp2-step-body">
            <span className="cp2-step-title">Bill uploaded</span>
            <span className="cp2-step-sub">{uploaded}</span>
          </span>
        </li>
        <li className={`cp2-step ${result ? 'is-done' : 'is-now'}`}>
          <span className="cp2-step-rail">
            <span className="cp2-step-dot">{result ? <Check size={9} strokeWidth={3.5} /> : null}</span>
            <span className="cp2-step-line" aria-hidden />
          </span>
          <span className="cp2-step-body">
            <span className="cp2-step-title">First look</span>
            <span className="cp2-step-sub">{result ? 'Done' : 'Underway now'}</span>
          </span>
        </li>
        {AHEAD.map((label, i) => (
          <li key={label} className="cp2-step">
            <span className="cp2-step-rail">
              <span className="cp2-step-dot" />
              {i < AHEAD.length - 1 && <span className="cp2-step-line" aria-hidden />}
            </span>
            <span className="cp2-step-body">
              <span className="cp2-step-title">{label}</span>
              <span className="cp2-step-sub">{i === 0 ? 'Starts when you do' : 'Up next'}</span>
            </span>
          </li>
        ))}
      </ol>
      {draft.due_date ? (
        <dl className="cp2-meta" aria-label="Case status">
          <div className="cp2-meta-row">
            <dt>Bill due</dt>
            <dd>{new Date(`${draft.due_date}T12:00:00`).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}</dd>
          </div>
        </dl>
      ) : (
        <form
          className="dc-due-ask"
          onSubmit={(e) => {
            e.preventDefault();
            void saveDueDate();
          }}
        >
          <label htmlFor="dc-due">When is the bill due?</label>
          <div>
            <input id="dc-due" type="date" value={dueInput} onChange={(e) => setDueInput(e.target.value)} />
            <button type="submit" disabled={!dueInput || savingDue}>
              {savingDue ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      )}
    </div>
  );

  const specialistCard = (
    <div className="cp2-side-card dc-who">
      <div className="cp2-side-title">Who works your case</div>
      <div className="dc-who-row">
        {SPECIALIST.photo ? (
          <img src={SPECIALIST.photo} alt="" className="dc-who-photo" />
        ) : (
          <span className="dc-who-photo dc-who-initial" aria-hidden>{SPECIALIST.name.charAt(0)}</span>
        )}
        <div>
          <p className="dc-who-name">{SPECIALIST.name}</p>
          <p className="dc-who-role">{SPECIALIST.role}</p>
        </div>
      </div>
      <p className="dc-who-line">{SPECIALIST.line}</p>
    </div>
  );

  const documentsCard = (
    <div className="cp2-side-card">
      <div className="cp2-side-title">Documents</div>
      {documents.map((doc) => (
        <button key={doc.path} type="button" className="cp2-doc" onClick={() => void openFile(doc.path)} title={`Open ${doc.name}`}>
          <span className="cp2-doc-icon"><FileText size={15} /></span>
          <span className="cp2-doc-body">
            <span className="cp2-doc-name">{doc.name}</span>
            <span className="cp2-doc-meta">{doc.meta}</span>
          </span>
          <Eye size={14} className="cp2-doc-dl" aria-hidden />
        </button>
      ))}
      <div className="dc-add">
        <button type="button" className="dc-add-btn" disabled={adding !== null} onClick={() => pageInputRef.current?.click()}>
          <Plus size={14} /> {adding === 'page' ? 'Uploading…' : 'Add a page'}
        </button>
        {!draft.eob_path && (
          <button type="button" className="dc-add-btn" disabled={adding !== null} onClick={() => eobInputRef.current?.click()}>
            <Plus size={14} /> {adding === 'eob' ? 'Uploading…' : 'Add your EOB'}
          </button>
        )}
      </div>
      <input ref={pageInputRef} type="file" accept="application/pdf,image/*" hidden onChange={(e) => { void addDocument('page', e.target.files?.[0]); e.target.value = ''; }} />
      <input ref={eobInputRef} type="file" accept="application/pdf,image/*" hidden onChange={(e) => { void addDocument('eob', e.target.files?.[0]); e.target.value = ''; }} />
      {addError && <p className="dc-send-error">{addError}</p>}
      <button type="button" className="dc-replace" onClick={() => void replaceFile()}>
        {confirmReplace ? 'Tap again to delete this upload and start over' : 'Upload a different file'}
      </button>
    </div>
  );

  /* Their other parked bills (migration 115). The list only shows once
     there's more than one, the "add another" link is always there. */
  const billsCard = (
    <div className="cp2-side-card">
      <div className="cp2-side-title">{drafts.length > 1 ? 'Your saved bills' : 'More bills?'}</div>
      {drafts.length > 1 &&
        drafts.map((d) => {
          const current = d.id === draft.id;
          return (
            <button
              key={d.id}
              type="button"
              className="cp2-doc"
              aria-current={current ? 'page' : undefined}
              disabled={current}
              onClick={() => {
                setSheetOpen(false);
                navigate(`/case/draft?id=${d.id}`);
              }}
              title={current ? 'Open now' : `Open ${d.provider_name || d.file_name || 'this bill'}`}
            >
              <span className="cp2-doc-icon"><FileText size={15} /></span>
              <span className="cp2-doc-body">
                <span className="cp2-doc-name">{d.provider_name || d.file_name || 'Your bill'}</span>
                <span className="cp2-doc-meta">
                  {current ? 'Open now' : `Uploaded ${new Date(d.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                </span>
              </span>
            </button>
          );
        })}
      <div className="dc-add">
        <button type="button" className="dc-add-btn" onClick={() => navigate('/add-bill?new=1')}>
          <Plus size={14} /> Upload another bill
        </button>
      </div>
    </div>
  );

  /* A run of fox messages shows the avatar once, on the last one, like
     any chat app. The others keep the space so the bubbles line up. */
  const fox = (show: boolean) => (
    <div className={`cp2-msg-avatar dc-avatar ${show ? '' : 'is-blank'}`} aria-hidden>
      {show && <img src="/images/mascot/fox-avatar.png" alt="" />}
    </div>
  );

  return (
    <AppSidebar>
      <div className="cp2 dc">
        <StartCasePaywall
          open={showPaywall}
          onClose={() => setShowPaywall(false)}
          email={user?.email}
          hasDraft
          draftId={draft.id}
          fileName={draft.file_name}
          dueDate={draft.due_date}
          trigger={paywallTrigger}
          annualHint={
            familyCount > 0
              ? 'You added family. Annual covers up to 6 bills a year for your household, $499.'
              : draft.extra_files.length >= 2
                ? 'More bills coming this year? Annual covers up to 6 for $499.'
                : null
          }
          onAsk={() => {
            setShowPaywall(false);
            inputRef.current?.focus();
          }}
        />
        <ExampleCaseModal open={showExample} onClose={() => setShowExample(false)} />

        <div className="cp2-layout">
          <header className="cp2-topbar">
            <div className="cp2-topbar-row">
              <button type="button" className="cp2-back" onClick={() => navigate('/')} aria-label="Back to home">
                <ArrowLeft size={16} />
              </button>
              <div className="cp2-titlewrap">
                <h1 className="cp2-title">{title}</h1>
                <div className="cp2-subrow">
                  {look?.amountDue ? <span className="cp2-amount num">{usd(look.amountDue)} bill</span> : null}
                  <span className="dc-pill">Not started</span>
                </div>
              </div>
              {!notBill && (
                <button type="button" className="dc-head-cta" onClick={() => start('draft_case_header')}>
                  Start my case
                </button>
              )}
              <button
                type="button"
                className="cp2-details-btn"
                onClick={() => {
                  phFreeLookAction('details_opened');
                  setSheetOpen(true);
                }}
              >
                <FolderOpen size={14} /> Details
              </button>
            </div>
          </header>

          <div className="cp2-chat-col">
            <div className="cp2-thread dc-thread" ref={threadRef}>
              <div className="cp2-msg is-them">
                {fox(false)}
                <div className="cp2-msg-col">
                  <div className="cp2-bubble">
                    Got it. {provider ? `Your ${provider} bill` : draft.file_name ? `"${draft.file_name}"` : 'Your bill'} is
                    saved to your account. Only you and the mediloop team can see it.
                  </div>
                </div>
              </div>

              {hasPhone === false ? (
                <div className="cp2-msg is-them dc-wide">
                  {fox(true)}
                  <div className="cp2-msg-col">
                    <div className="cp2-bubble">
                      {result ? 'Your first look is ready.' : "I'm taking a first look now."} Before I show you, what's the best number for you?
                    </div>
                    <form
                      className="dc-phone-gate"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void saveGatePhone();
                      }}
                    >
                      <label htmlFor="dc-gate-phone">Phone number</label>
                      <div>
                        <input
                          id="dc-gate-phone"
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          placeholder="(555) 123-4567"
                          value={gatePhone}
                          onChange={(e) => {
                            setGatePhone(e.target.value);
                            setGateError(null);
                          }}
                        />
                        <button type="submit" disabled={!gatePhone.trim() || savingGate}>
                          {savingGate ? 'Saving…' : 'Show my first look'}
                        </button>
                      </div>
                      {gateError ? (
                        <p className="dc-send-error">{gateError}</p>
                      ) : (
                        <p className="dc-phone-fine">In case we need to reach you about your bill. We may call or text.</p>
                      )}
                    </form>
                  </div>
                </div>
              ) : !result || hasPhone === null ? (
                <div className="cp2-msg is-them" aria-busy="true">
                  {fox(true)}
                  <div className="cp2-msg-col">
                    <div className="cp2-bubble">
                      Taking a first look. This takes a few seconds.
                      <ul className="fl-steps dc-steps">
                        {READING_STEPS.map((label, i) => (
                          <li key={label} className={i < readingStep ? 'is-done' : i === readingStep ? 'is-active' : ''}>
                            <span className="fl-step-dot">{i < readingStep ? <Check size={12} strokeWidth={3} /> : null}</span>
                            {label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="cp2-msg is-them dc-wide">
                  {fox(true)}
                  <div className="cp2-msg-col">
                    <div className="cp2-bubble">{firstLook}</div>
                    {notBill && (
                      <div className="dc-notbill">
                        <button
                          type="button"
                          className="dc-cta"
                          onClick={() => {
                            phFreeLookAction(`replace_after_${result?.status}`);
                            void (async () => {
                              await deleteBillDraft(draft, true);
                              navigate('/add-bill?new=1', { replace: true });
                            })();
                          }}
                        >
                          Upload my bill
                        </button>
                      </div>
                    )}
                    {found > 0 && look && (
                      <div className="dc-look">
                        <ol className="dc-look-list">
                          {look.findings.map((f, i) => (
                            <li key={i}>
                              <span className="dc-look-n">{i + 1}</span>
                              <div>
                                <strong>{f.title}</strong>
                                <p>{f.detail}</p>
                              </div>
                            </li>
                          ))}
                          {/* The rest are only counted. These rows are placeholders,
                              there's nothing behind the blur to read. */}
                          {Array.from({ length: Math.min(look.moreFindings, 3) }).map((_, i) => (
                            <li key={`locked-${i}`} className="is-locked" aria-hidden>
                              <span className="dc-look-n"><Lock size={11} /></span>
                              <div className="dc-look-ghost">
                                <span style={{ width: `${[62, 48, 56][i]}%` }} />
                                <span style={{ width: `${[84, 72, 78][i]}%` }} />
                              </div>
                            </li>
                          ))}
                        </ol>
                        <p className="dc-look-lock">
                          <Lock size={13} aria-hidden />
                          {look.moreFindings > 0
                            ? `${look.moreFindings} more ${look.moreFindings === 1 ? 'finding' : 'findings'}, plus the exact charges and what to say to the hospital, open up when you start.`
                            : 'The exact charges and what to say to the hospital come with your case.'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {shown && (
                <>
                {draft.due_date && daysUntil(draft.due_date) <= 30 && (
                  <p className={`dc-due ${daysUntil(draft.due_date) <= 7 ? 'is-soon' : ''}`}>
                    {daysUntil(draft.due_date) < 0
                      ? `This bill was due ${shortDate(draft.due_date)}. Starting now still helps, we can ask the hospital to hold it while we work on it.`
                      : `Due ${shortDate(draft.due_date)}, ${daysUntil(draft.due_date) === 0 ? 'today' : `in ${daysUntil(draft.due_date)} day${daysUntil(draft.due_date) === 1 ? '' : 's'}`}. Starting before then gives us time to ask the hospital to hold it while we work on it.`}
                  </p>
                )}
                {!notBill && (
                <section className="dc-offer" aria-label="Start your case">
                  <div className="dc-offer-head">
                    <span className="dc-offer-eyebrow">Your case is ready to start</span>
                    {estimate ? (
                      <>
                        <p className="dc-offer-big num">
                          {usd(estimate.low)} to {usd(estimate.high)}
                        </p>
                        <p className="dc-offer-sub">What bills this size usually come down by. Typical, not a quote for yours.</p>
                      </>
                    ) : (
                      <p className="dc-offer-big">A specialist takes it from here</p>
                    )}
                    {/* Real numbers only, and only once there are enough
                        closed cases for a median to mean something. */}
                    {results && results.cases >= 5 && results.medianSaved ? (
                      <p className="dc-offer-proof">
                        Our closed cases saved a median of <strong className="num">{usd(results.medianSaved)}</strong>.
                      </p>
                    ) : null}
                  </div>

                  <ol className="dc-offer-steps">
                    <li>
                      <span>1</span>
                      <div>
                        <strong>We check every line</strong>
                        <p>Our AI-powered investigation goes through the bill, and a specialist reviews what it finds.</p>
                      </div>
                    </li>
                    <li>
                      <span>2</span>
                      <div>
                        <strong>We deal with the hospital</strong>
                        <p>We get the itemized bill and negotiate the price. You don't make any calls.</p>
                      </div>
                    </li>
                    <li>
                      <span>3</span>
                      <div>
                        <strong>Money back if we can't save you $129</strong>
                        <p>If we don't save you at least $129, you get a full refund the same day.</p>
                      </div>
                    </li>
                  </ol>

                  <div className="dc-offer-foot">
                    <div className="dc-offer-price">
                      <span className="num">$129</span> flat, one time
                    </div>
                    <button ref={offerCtaRef} type="button" className="dc-cta" onClick={() => start('draft_case_thread')}>
                      Let's get the work started
                    </button>
                  </div>
                  <button
                    type="button"
                    className="dc-example-link"
                    onClick={() => {
                      phFreeLookAction('example_case_opened');
                      setShowExample(true);
                    }}
                  >
                    See an example of a finished case
                  </button>
                </section>
                )}
                </>
              )}

              {shown && (
                <div className="dc-ask-divider">
                  <span>{hasAsked ? 'Your messages with the team' : 'Questions first? Message the team below'}</span>
                </div>
              )}

              {messages.map((m, i) => {
                const mine = m.sender_type === 'customer';
                const next = messages[i + 1];
                const lastOfRun = !next || next.sender_type !== m.sender_type;
                return (
                  <div key={m.id} className={`cp2-msg ${mine ? 'is-me' : 'is-them'}`}>
                    {!mine && fox(lastOfRun)}
                    <div className="cp2-msg-col">
                      {!mine && (i === 0 || messages[i - 1].sender_type !== 'admin') && (
                        <span className="dc-sender">{m.sender_name || 'mediloop team'}</span>
                      )}
                      <div className="cp2-bubble">{m.message}</div>
                      {lastOfRun && <div className="cp2-msg-meta">{timeOf(m.created_at)}</div>}
                    </div>
                  </div>
                );
              })}

              {lastIsMine && (
                <p className="dc-sent-note">
                  Sent to the team. A person reads every message and replies here. We'll email you when they do.
                </p>
              )}
              {lastIsMine && textsOn === false && (
                <form
                  className="dc-text-ask"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void savePhone();
                  }}
                >
                  <label htmlFor="dc-phone">Rather get a text when they reply?</label>
                  <div>
                    <input
                      id="dc-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="(555) 123-4567"
                      value={phoneInput}
                      onChange={(e) => {
                        setPhoneInput(e.target.value);
                        setPhoneError(null);
                      }}
                    />
                    <button type="submit" disabled={!phoneInput.trim() || savingPhone}>
                      {savingPhone ? 'Saving…' : 'Text me'}
                    </button>
                  </div>
                  {phoneError ? (
                    <p className="dc-send-error">{phoneError}</p>
                  ) : (
                    <p className="dc-text-fine">Only about your bill. Msg &amp; data rates may apply. Reply STOP any time.</p>
                  )}
                </form>
              )}
              {lastIsMine && phoneJustSaved && (
                <p className="dc-sent-note">Got it. We'll text you when the team replies.</p>
              )}
            </div>

            <div className="cp2-composer-wrap dc-composer">
              {shown && !notBill && !offerCtaVisible && (
                <button type="button" className="dc-cta dc-bar-cta" onClick={() => start('draft_case_composer')}>
                  Let's get the work started
                </button>
              )}
              {!hasAsked && (
                <div className="cp2-chips" role="group" aria-label="Suggested questions">
                  {QUESTION_CHIPS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      className="cp2-chip"
                      onClick={() => {
                        setText(q);
                        inputRef.current?.focus();
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              <div className="cp2-composer">
                <textarea
                  ref={inputRef}
                  className="cp2-input dc-input"
                  placeholder="Ask the team a question…"
                  value={text}
                  maxLength={4000}
                  rows={1}
                  onChange={(e) => {
                    setText(e.target.value);
                    setSendError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                />
                <button type="button" className="cp2-send" onClick={() => void send()} disabled={!text.trim() || sending} aria-label="Send">
                  <Send size={16} />
                </button>
              </div>
              {sendError && <p className="dc-send-error">{sendError}</p>}
            </div>
          </div>

          <aside className="cp2-side">
            {specialistCard}
            {progressCard}
            {documentsCard}
            {billsCard}
          </aside>
        </div>

        {sheetOpen && (
          <>
            <div className="cp2-sheet-backdrop" onClick={() => setSheetOpen(false)} />
            <div className="cp2-sheet" role="dialog" aria-label="Case details">
              <div className="cp2-sheet-handle" aria-hidden />
              <div className="cp2-sheet-head">
                <div className="cp2-side-title">Case details</div>
                <button type="button" className="cp2-sheet-close" onClick={() => setSheetOpen(false)} aria-label="Close details">
                  <XIcon size={16} />
                </button>
              </div>
              {specialistCard}
              {progressCard}
              {documentsCard}
              {billsCard}
            </div>
          </>
        )}
      </div>
    </AppSidebar>
  );
}
